import React, { useContext, useState, useMemo } from "react";
import { DataContext } from "./index";
import { AgGridReact } from "ag-grid-react";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import {
  Box,
  IconButton,
  Tooltip,
  useTheme,
  Button,
  Menu,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Backdrop,
  CircularProgress,
  TablePagination,
} from "@mui/material";
import { tokens } from "../../theme";
import { Link } from "react-router-dom";
import DateFormatter from "helper/dateFormartter";
import DateTimeFormatter from "helper/dateTimeFormartter";
import InfoIcon from "@mui/icons-material/Info";
import ErrorIcon from "@mui/icons-material/Error";
import ErrorAlert from "helper/errorAlert";
import SuccessAlert from "helper/successAlert";
import axios from "axios";
import moment from "moment";
import { UploadFile, ViewColumn, FilterAlt, FileDownload } from "@mui/icons-material";
import DeleteForeverOutlinedIcon from "@mui/icons-material/DeleteForeverOutlined";
import { useApiClient } from "context/ApiClientContext";
import Papa from "papaparse";
import AgGridHeader from "components/agGridHeader";

// Restoring the classic CSS imports so the legacy styles load correctly
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-material.css";

// Register all community modules globally for AG Grid v36
ModuleRegistry.registerModules([AllCommunityModule]);

// Friendly column labels to display in the Columns Selector checklist
const COLUMN_LABELS = {
  buyer_Name: "Customer Name",
  docNum: "Invoice Number",
  status: "Status",
  docDate: "Invoice Date",
  typeCode_Value: "Type Code Value",
  docDueDate: "Invoice Due Date",
  longid: "Long Id",
  version: "Version",
  remark: "Remark",
  addedDate: "Added Date",
  updatedDate: "Updated Date",
  currency: "Currency",
  total_PayableAmt: "Total Amount",
  submissionuuid: "Submission UUID",
  docuuid: "Document UUID",
  submitdate: "Submit IRB Date",
  validatedate: "IRB Validated Date",
};

export default function AgGridListing(props) {
  const { isCheckbox, isSoftDelete } = props;
  const rows = useContext(DataContext);
  const dateFormatter = DateFormatter();
  const dateTimeFormatter = DateTimeFormatter();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const apiClient = useApiClient();

  const [selectedRows, setSelectedRows] = useState([]);
  const [open, setOpen] = useState(false);

  // Set floating filter to false by default (closed)
  const [floatingFilter, setFloatingFilter] = useState(false);

  // Store gridApi in state for robust access
  const [gridApi, setGridApi] = useState(null);

  // Anchor for Columns Dropdown Menu
  const [columnsMenuAnchor, setColumnsMenuAnchor] = useState(null);

  // Pagination variables bound to parent state props
  const page = props.currentPage || 0;
  const pageSize = props.pageSize || 100;

  // Track Column Visibility State
  const [columnVisibility, setColumnVisibility] = useState({
    buyer_Name: true,
    docNum: true,
    status: true,
    docDate: true,
    typeCode_Value: true,
    docDueDate: true,
    longid: true,
    version: false,
    remark: false,
    addedDate: true,
    updatedDate: true,
    currency: false,
    total_PayableAmt: true,
    submissionuuid: true,
    docuuid: true,
    submitdate: true,
    validatedate: true,
  });

  const handleClose = () => setOpen(false);
  const handleOpen = () => setOpen(true);

  const getCurrentDatetime = () => {
    return moment().format("YYYYMMDDHHmm");
  };

  const formatAmount = (value, currency) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "MYR",
    })
      .format(value)
      .replace(/^(\D+)/, "");
  };

  // Define column configuration
  const columnDefs = useMemo(() => {
    const defs = [
      {
        headerName: "",
        field: "actions",
        width: 60,
        filter: false,
        sortable: false,
        pinned: "left",
        cellRenderer: (params) => {
          if (!params.data) return null;
          return (
            <Link to={`/account-receivable/${params.data.id}`}>
              <IconButton color="primary" size="small" style={{ padding: 4 }}>
                <Tooltip title="View Transaction" placement="right">
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </IconButton>
            </Link>
          );
        },
      },
      {
        headerName: "Customer Name",
        field: "buyer_Name",
        width: 300,
        hide: !columnVisibility.buyer_Name,
      },
      {
        headerName: "Invoice Number",
        field: "docNum",
        width: 150,
        hide: !columnVisibility.docNum,
      },
      {
        headerName: "Status",
        field: "status",
        width: 150,
        hide: !columnVisibility.status,
        cellRenderer: (params) => {
          if (!params.data) return null;
          return (
            <div style={{ display: "flex", alignItems: "center" }}>
              <span>{params.value}</span>
              {params.data.remark && (
                <IconButton size="small" style={{ marginLeft: 4, padding: 2 }}>
                  <Tooltip title={params.data.remark} placement="bottom">
                    <ErrorIcon color="error" fontSize="small" />
                  </Tooltip>
                </IconButton>
              )}
            </div>
          );
        },
      },
      {
        headerName: "Invoice Date",
        field: "docDate",
        width: 200,
        hide: !columnVisibility.docDate,
        valueGetter: (params) =>
          params.data?.docDate ? new Date(params.data.docDate) : null,
        valueFormatter: (params) =>
          params.value ? dateFormatter.format(params.value) : "",
        filterValueGetter: (params) =>
          params.data?.docDate ? dateFormatter.format(new Date(params.data.docDate)) : "",
      },
      {
        headerName: "Type Code Value",
        field: "typeCode_Value",
        width: 150,
        hide: !columnVisibility.typeCode_Value,
      },
      {
        headerName: "Invoice Due Date",
        field: "docDueDate",
        width: 150,
        hide: !columnVisibility.docDueDate,
        valueGetter: (params) =>
          params.data?.docDueDate ? new Date(params.data.docDueDate) : null,
        valueFormatter: (params) =>
          params.value ? dateFormatter.format(params.value) : "",
        filterValueGetter: (params) =>
          params.data?.docDueDate ? dateFormatter.format(new Date(params.data.docDueDate)) : "",
      },
      {
        headerName: "Long Id",
        field: "longid",
        width: 250,
        hide: !columnVisibility.longid,
      },
      {
        headerName: "Version",
        field: "version",
        width: 120,
        hide: !columnVisibility.version,
      },
      {
        headerName: "Remark",
        field: "remark",
        width: 120,
        hide: !columnVisibility.remark,
      },
      {
        headerName: "Added Date",
        field: "addedDate",
        width: 200,
        hide: !columnVisibility.addedDate,
        valueGetter: (params) =>
          params.data?.addedDate ? new Date(params.data.addedDate) : null,
        valueFormatter: (params) =>
          params.value ? dateTimeFormatter.format(params.value) : "",
        filterValueGetter: (params) =>
          params.data?.addedDate ? dateTimeFormatter.format(new Date(params.data.addedDate)) : "",
      },
      {
        headerName: "Updated Date",
        field: "updatedDate",
        width: 200,
        hide: !columnVisibility.updatedDate,
        valueGetter: (params) =>
          params.data?.updatedDate ? new Date(params.data.updatedDate) : null,
        valueFormatter: (params) =>
          params.value ? dateTimeFormatter.format(params.value) : "",
        filterValueGetter: (params) =>
          params.data?.updatedDate ? dateTimeFormatter.format(new Date(params.data.updatedDate)) : "",
      },
      {
        headerName: "Currency",
        field: "currency",
        width: 120,
        hide: !columnVisibility.currency,
      },
      {
        headerName: "Total Amount",
        field: "total_PayableAmt",
        width: 150,
        hide: !columnVisibility.total_PayableAmt,
        type: "numericColumn",
        valueFormatter: (params) => {
          if (params.value === undefined || params.value === null) return "";
          return formatAmount(params.value, params.data?.currency);
        },
      },
      {
        headerName: "Submission UUID",
        field: "submissionuuid",
        width: 250,
        hide: !columnVisibility.submissionuuid,
        valueGetter: (params) => params.data?.submissionuuid || "-",
      },
      {
        headerName: "Document UUID",
        field: "docuuid",
        width: 250,
        hide: !columnVisibility.docuuid,
        valueGetter: (params) => params.data?.docuuid || "-",
      },
      {
        headerName: "Submit IRB Date",
        field: "submitdate",
        width: 200,
        hide: !columnVisibility.submitdate,
        valueGetter: (params) =>
          params.data?.submitdate ? new Date(params.data.submitdate) : null,
        valueFormatter: (params) => {
          if (!params.value) return "-";
          const currentDate = new Date();
          const fortyYearsAgo = new Date();
          fortyYearsAgo.setFullYear(currentDate.getFullYear() - 40);
          if (params.value < fortyYearsAgo || params.value > currentDate) {
            return "-";
          }
          return dateTimeFormatter.format(params.value);
        },
        filterValueGetter: (params) => {
          if (!params.data?.submitdate) return "-";
          const dateVal = new Date(params.data.submitdate);
          const currentDate = new Date();
          const fortyYearsAgo = new Date();
          fortyYearsAgo.setFullYear(currentDate.getFullYear() - 40);
          if (dateVal < fortyYearsAgo || dateVal > currentDate) {
            return "-";
          }
          return dateTimeFormatter.format(dateVal);
        },
      },
      {
        headerName: "IRB Validated Date",
        field: "validatedate",
        width: 200,
        hide: !columnVisibility.validatedate,
        valueGetter: (params) =>
          params.data?.validatedate ? new Date(params.data.validatedate) : null,
        valueFormatter: (params) => {
          if (!params.value) return "-";
          const currentDate = new Date();
          const fortyYearsAgo = new Date();
          fortyYearsAgo.setFullYear(currentDate.getFullYear() - 40);
          if (params.value < fortyYearsAgo || params.value > currentDate) {
            return "-";
          }
          return dateTimeFormatter.format(params.value);
        },
        filterValueGetter: (params) => {
          if (!params.data?.validatedate) return "-";
          const dateVal = new Date(params.data.validatedate);
          const currentDate = new Date();
          const fortyYearsAgo = new Date();
          fortyYearsAgo.setFullYear(currentDate.getFullYear() - 40);
          if (dateVal < fortyYearsAgo || dateVal > currentDate) {
            return "-";
          }
          return dateTimeFormatter.format(dateVal);
        },
      },
    ];

    // If checkbox selection is active (for submit or delete)
    if (isCheckbox || isSoftDelete) {
      defs.unshift({
        headerName: "",
        field: "rowSelection",
        width: 50,
        checkboxSelection: true,
        headerCheckboxSelection: true,
        pinned: "left",
        filter: false,
        sortable: false,
      });
    }

    // Explicitly inject the floatingFilter property into every filterable column.
    return defs.map((def) => {
      if (def.filter !== false) {
        return { ...def, floatingFilter };
      }
      return def;
    });
  }, [columnVisibility, isCheckbox, isSoftDelete, dateFormatter, dateTimeFormatter, floatingFilter]);

  // Default Column Properties
  const defaultColDef = useMemo(() => {
    return {
      sortable: true,
      filter: true,
      resizable: true,
      suppressMenu: true, // Suppress the Enterprise column menu button
      headerComponent: AgGridHeader,
    };
  }, []);

  // Handle grid initialization
  const onGridReady = (params) => {
    setGridApi(params.api);
  };

  // Map AG Grid column fields to exact database column casing in DocHead table
  const DB_COLUMNS = {
    buyer_Name: "Buyer_Name",
    supplier_Name: "Supplier_Name",
    docNum: "DocNum",
    status: "Status",
    docDate: "DocDate",
    typeCode_Value: "TypeCode_Value",
    docDueDate: "DocDueDate",
    longid: "longid",
    version: "Version",
    remark: "Remark",
    addedDate: "AddedDate",
    updatedDate: "UpdatedDate",
    currency: "Currency",
    total_PayableAmt: "Total_PayableAmt",
    submissionuuid: "submissionuuid",
    docuuid: "docuuid",
    submitdate: "submitdate",
    validatedate: "validatedate"
  };

  // Handle AG Grid sorting changed event
  const onSortChanged = (event) => {
    if (!event.api) return;
    const sortModel = event.api.getColumnState().filter((col) => col.sort != null);
    let sortStr = "id desc"; // default fallback
    if (sortModel.length > 0) {
      const activeSort = sortModel[0];
      const dbCol = DB_COLUMNS[activeSort.colId] || activeSort.colId;
      sortStr = `${dbCol} ${activeSort.sort}`;
    }
    if (props.onGridStateChange && sortStr !== (props.sortCriteria || "id desc")) {
      props.onGridStateChange(0, pageSize, sortStr, props.extraFilters || "");
    }
  };

  // Handle AG Grid filtering changed event
  const onFilterChanged = (event) => {
    if (!event.api) return;
    const filterModel = event.api.getFilterModel();
    const filterClauses = [];

    Object.keys(filterModel).forEach((colId) => {
      const condition = filterModel[colId];
      const dbCol = DB_COLUMNS[colId] || colId;

      if (condition.filterType === "text") {
        const val = condition.filter.replace(/'/g, "''").toLowerCase();
        if (condition.type === "contains") {
          filterClauses.push(`LOWER(${dbCol}) LIKE '%${val}%'`);
        } else if (condition.type === "equals") {
          filterClauses.push(`LOWER(${dbCol}) = '${val}'`);
        } else if (condition.type === "startsWith") {
          filterClauses.push(`LOWER(${dbCol}) LIKE '${val}%'`);
        } else if (condition.type === "endsWith") {
          filterClauses.push(`LOWER(${dbCol}) LIKE '%${val}'`);
        }
      } else if (condition.filterType === "number") {
        const val = condition.filter;
        if (condition.type === "equals") {
          filterClauses.push(`${dbCol} = ${val}`);
        } else if (condition.type === "greaterThan") {
          filterClauses.push(`${dbCol} > ${val}`);
        } else if (condition.type === "lessThan") {
          filterClauses.push(`${dbCol} < ${val}`);
        }
      }
    });

    const extraFiltersStr = filterClauses.join(" and ");
    if (props.onGridStateChange && extraFiltersStr !== (props.extraFilters || "")) {
      props.onGridStateChange(0, pageSize, props.sortCriteria || "id desc", extraFiltersStr);
    }
  };

  // Handle row selection changes
  const onSelectionChanged = (event) => {
    const nodes = event.api.getSelectedNodes();
    const rowsSelected = nodes.map((node) => ({ id: node.data.id }));
    setSelectedRows(rowsSelected);
  };

  // Handle click on the header checkbox to clear selection if it's in indeterminate state
  const handleHeaderCheckboxClick = (event) => {
    if (!gridApi) return;
    const wrapper = event.target.closest(".ag-header-select-all .ag-checkbox-input-wrapper");
    if (wrapper && wrapper.classList.contains("ag-indeterminate")) {
      event.stopPropagation();
      event.preventDefault();
      gridApi.deselectAll();
    }
  };

  // Toggle dynamic column visibility (100% declarative React state change)
  const toggleColumnVisibility = (field) => {
    setColumnVisibility((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  // Handle page index change from TablePagination
  const handlePageChange = (event, newPage) => {
    if (props.onGridStateChange) {
      props.onGridStateChange(newPage, pageSize, props.sortCriteria || "id desc", props.extraFilters || "");
    }
  };

  // Handle page size change from TablePagination
  const handleRowsPerPageChange = (event) => {
    const newSize = parseInt(event.target.value, 10);
    if (props.onGridStateChange) {
      props.onGridStateChange(0, newSize, props.sortCriteria || "id desc", props.extraFilters || "");
    }
  };

  // Handle bulk submission
  const bulkSubmit = async (isVoid = false) => {
    const newArray = selectedRows.map((data) => {
      if (isVoid) {
        return {
          id: data.id,
          companyId: localStorage.getItem("companyId"),
          status: "Void",
          userid: localStorage.getItem("loggedInID"),
        };
      } else {
        return {
          id: data.id,
          companyId: localStorage.getItem("companyId"),
          status: "Pending",
          remark: "",
          userid: localStorage.getItem("loggedInID"),
          irbinvoice: "",
        };
      }
    });

    try {
      handleOpen();
      const response = await axios.put(
        `${apiClient}/${localStorage.getItem("companyId")}/document/headerstatus`,
        {
          bulkdocument: newArray,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      const datas = response.data;
      const message = isVoid
        ? "The selected invoices have been voided."
        : "The selected invoices are waiting to send to IRB";

      SuccessAlert(datas, message);
    } catch (error) {
      ErrorAlert(error);
      console.error("An error occurred:", error.response);
    } finally {
      setTimeout(() => {
        handleClose();
        window.location.reload();
      }, 2000);
    }
  };

  // Handle CSV Export using existing backend header_v2 endpoint with pagesize=totalCount
  const handleExportCsv = async () => {
    const companyId = localStorage.getItem("companyId");
    const encodedSource = encodeURIComponent("docSource='AR'");

    let filter = "";
    switch (props.tabIndex) {
      case 0:
        filter = encodeURIComponent(" and status != 'void'");
        break;
      case 1:
      case 2:
      case 3:
      case 4:
        if (props.tabIndex < 5) {
          const statusNames = ["", "error", "rejected", "dispute", "open"];
          const statusName = statusNames[props.tabIndex];
          filter = encodeURIComponent(` and status='${statusName}'`);
        }
        break;
    }

    if (props.extraFilters) {
      filter += encodeURIComponent(` and (${props.extraFilters})`);
    }

    const sort = props.sortCriteria || "id desc";
    const totalCount = props.totalRecord || 0;

    if (totalCount === 0) {
      console.warn("No records to export.");
      return;
    }

    try {
      // Fetch the full list of matching records using existing pagination API
      const response = await axios.get(
        `${apiClient}/${companyId}/document/header_v2?sortcriteria=${encodeURIComponent(sort)}&filters=${encodedSource}${filter}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            Accept: "application/json",
          },
          params: {
            pagenum: 1,
            pagesize: totalCount,
          },
        }
      );

      const { docHead } = response.data;
      if (!docHead || docHead.length === 0) {
        console.warn("No data returned for export.");
        return;
      }

      // Format headers and columns matching screen representation
      const formattedRows = docHead.map((row) => {
        const formattedRow = {};
        
        const exportCols = [
          { field: "buyer_Name", label: "Customer Name" },
          { field: "docNum", label: "Invoice Number" },
          { field: "status", label: "Status" },
          { field: "docDate", label: "Invoice Date", isDate: true },
          { field: "typeCode_Value", label: "Type Code Value" },
          { field: "docDueDate", label: "Invoice Due Date", isDate: true },
          { field: "longid", label: "Long Id" },
          { field: "version", label: "Version" },
          { field: "remark", label: "Remark" },
          { field: "addedDate", label: "Added Date", isDateTime: true },
          { field: "updatedDate", label: "Updated Date", isDateTime: true },
          { field: "currency", label: "Currency" },
          { field: "total_PayableAmt", label: "Total Amount", isAmount: true },
          { field: "submissionuuid", label: "Submission UUID" },
          { field: "docuuid", label: "Document UUID" },
          { field: "submitdate", label: "Submit IRB Date", isSpecialDateTime: true },
          { field: "validatedate", label: "IRB Validated Date", isSpecialDateTime: true },
        ];

        exportCols.forEach((col) => {
          if (columnVisibility[col.field] !== false) {
            let val = row[col.field];
            try {
              if (col.isAmount) {
                val = val !== undefined && val !== null ? formatAmount(val, row.currency) : "";
              } else if (col.isDate) {
                val = val ? dateFormatter.format(new Date(val)) : "";
              } else if (col.isDateTime) {
                val = val ? dateTimeFormatter.format(new Date(val)) : "";
              } else if (col.isSpecialDateTime) {
                if (!val) {
                  val = "-";
                } else {
                  const dateVal = new Date(val);
                  const currentDate = new Date();
                  const fortyYearsAgo = new Date();
                  fortyYearsAgo.setFullYear(currentDate.getFullYear() - 40);
                  if (dateVal < fortyYearsAgo || dateVal > currentDate) {
                    val = "-";
                  } else {
                    val = dateTimeFormatter.format(dateVal);
                  }
                }
              }
            } catch (e) {
              console.warn(`Error formatting column ${col.field}:`, e);
            }
            formattedRow[col.label] = val;
          }
        });

        return formattedRow;
      });

      const cleanedRows = formattedRows.map((row) => {
        const cleaned = {};
        Object.keys(row).forEach((key) => {
          if (key && key.trim() !== "") {
            cleaned[key] = row[key];
          }
        });
        return cleaned;
      });

      const csv = Papa.unparse(cleanedRows);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", `QInvo_Export_${getCurrentDatetime()}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 100);

    } catch (err) {
      console.error("Failed to generate CSV export:", err);
    }
  };

  return (
    <div style={{ height: 600, width: "100%", display: "flex", flexDirection: "column", marginTop: "8px" }}>
      {/* Background overlay loader */}
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={open}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Styled Grid Header Wrapper (Mimics CustomToolbar from DataGridPro) */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          backgroundColor: (theme) =>
            theme.palette.mode === "light"
              ? theme.palette.grey[100]
              : theme.palette.grey[900],
          padding: "6px 16px",
          gap: 1.5,
          borderBottom: (theme) => `1px solid ${theme.palette.mode === "dark" ? colors.grey[700] : "#e0e0e0"}`,
          borderRadius: "4px 4px 0 0",
        }}
      >
        {/* Spacer */}
        <Box sx={{ flexGrow: 1 }} />

        {/* Columns Chooser Button */}
        <Button
          startIcon={<ViewColumn />}
          onClick={(e) => setColumnsMenuAnchor(e.currentTarget)}
          sx={{
            color: (theme) => theme.palette.mode === "dark" ? "#ffffff" : "#333333",
            textTransform: "uppercase",
            fontSize: "12px",
            fontWeight: "bold",
            "&:hover": {
              backgroundColor: (theme) => theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.04)",
            },
          }}
          size="small"
        >
          Columns
        </Button>
        <Menu
          anchorEl={columnsMenuAnchor}
          open={Boolean(columnsMenuAnchor)}
          onClose={() => setColumnsMenuAnchor(null)}
          PaperProps={{
            style: {
              maxHeight: 350,
              backgroundColor: colors.grey[800],
              color: colors.grey[100],
              border: `1px solid ${colors.grey[700]}`,
            },
          }}
        >
          {Object.keys(columnVisibility).map((field) => (
            <MenuItem key={field} style={{ padding: "4px 16px" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={columnVisibility[field]}
                    onChange={() => toggleColumnVisibility(field)}
                    sx={{
                      color: colors.grey[200],
                      "&.Mui-checked": {
                        color: "#184478",
                      },
                    }}
                  />
                }
                label={COLUMN_LABELS[field] || field}
                style={{ margin: 0 }}
              />
            </MenuItem>
          ))}
        </Menu>

        {/* Export Button */}
        <Button
          startIcon={<FileDownload />}
          onClick={handleExportCsv}
          sx={{
            color: (theme) => theme.palette.mode === "dark" ? "#ffffff" : "#333333",
            textTransform: "uppercase",
            fontSize: "12px",
            fontWeight: "bold",
            "&:hover": {
              backgroundColor: (theme) => theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.04)",
            },
          }}
          size="small"
        >
          Export CSV
        </Button>

        {/* Submit Actions */}
        {isCheckbox && (
          <Button
            disabled={!selectedRows.length}
            startIcon={<UploadFile />}
            onClick={() => bulkSubmit(false)}
            sx={{
              color: (theme) => !selectedRows.length ? "inherit" : (theme.palette.mode === "dark" ? "#ffffff" : "#333333"),
              textTransform: "uppercase",
              fontSize: "12px",
              fontWeight: "bold",
              "&:hover": {
                backgroundColor: (theme) => theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.04)",
              },
            }}
            size="small"
          >
            Submit
          </Button>
        )}
        {isSoftDelete && (
          <Button
            disabled={!selectedRows.length}
            startIcon={<DeleteForeverOutlinedIcon />}
            onClick={() => bulkSubmit(true)}
            sx={{
              color: (theme) => !selectedRows.length ? "inherit" : (theme.palette.mode === "dark" ? "#ffffff" : "#333333"),
              textTransform: "uppercase",
              fontSize: "12px",
              fontWeight: "bold",
              "&:hover": {
                backgroundColor: (theme) => theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.04)",
              },
            }}
            size="small"
          >
            Delete
          </Button>
        )}
      </Box>

      {/* Grid container with custom theme and styling */}
      <Box
        className="ag-theme-material"
        style={{ height: "calc(100% - 95px)", width: "100%" }}
        onClickCapture={handleHeaderCheckboxClick}
        sx={{
          "& .ag-root-wrapper": {
            border: "none",
            backgroundColor: `${colors.grey[800]} !important`,
            fontFamily: "inherit",
          },
          "& .ag-header": {
            backgroundColor: `${colors.grey[600]} !important`,
            borderBottom: "none",
          },
          "& .ag-header-row": {
            borderBottom: "none",
          },
          "& .ag-header-cell": {
            borderRight: "1px solid transparent !important",
            backgroundColor: `${colors.grey[600]} !important`,
            transition: "border-color 0.15s ease-in-out",
          },
          "& .ag-header:hover .ag-header-cell": {
            borderRight: `1px solid ${colors.grey[700]} !important`,
          },
          "& .ag-header-cell-label": {
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          },
          "& .ag-header-cell-text": {
            color: `${colors.primary[100]} !important`,
            fontWeight: "bold",
            fontSize: "13px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: "1 1 auto",
          },
          "& .ag-header-cell-label .ag-icon": {
            fontSize: "11px !important",
            marginLeft: "4px !important",
            flex: "0 0 auto",
            opacity: "0.8",
          },
          "& .ag-header-cell .ag-icon-filter": {
            display: "none !important",
          },
          "& .ag-header-cell:hover .ag-icon-filter, & .ag-header-cell.ag-header-cell-filtered .ag-icon-filter": {
            display: "inline-block !important",
          },
          "& .ag-pinned-left-header": {
            backgroundColor: `${colors.grey[600]} !important`,
            borderRight: `1px solid ${colors.grey[700]}`,
          },
          "& .ag-floating-filter-button": {
            display: "none !important",
          },
          "& .ag-row": {
            borderBottom: `1px solid ${colors.grey[700]} !important`,
            backgroundColor: `${colors.grey[800]} !important`,
            color: `${colors.grey[100]} !important`,
          },
          "& .ag-row-hover": {
            backgroundColor: `${colors.grey[700]} !important`,
          },
          "& .ag-row-selected": {
            backgroundColor: `${colors.grey[600]} !important`,
          },
          "& .ag-cell": {
            borderRight: "none",
            display: "flex",
            alignItems: "center",
            fontSize: "13px",
            overflow: "hidden",
          },
          "& .ag-cell-value": {
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            width: "100%",
          },
          "& .ag-pinned-left-cols-container": {
            backgroundColor: `${colors.grey[800]} !important`,
            borderRight: `1px solid ${colors.grey[700]}`,
          },
          "& .ag-pinned-left-cols-container .ag-cell": {
            backgroundColor: `${colors.grey[800]} !important`,
          },
          "& .ag-checkbox-input-wrapper.ag-checked::after, & .ag-checkbox-input-wrapper.ag-indeterminate::after": {
            color: "#184478 !important",
          },
          "& .ag-floating-filter-input": {
            backgroundColor: `${colors.grey[900]} !important`,
            color: `${colors.grey[100]} !important`,
            border: `1px solid ${colors.grey[600]}`,
            borderRadius: "4px",
            padding: "2px 6px",
          },
          "& .ag-floating-filter-input:focus-within": {
            border: `1px solid ${colors.greenAccent?.[500] || "#4caf50"}`,
          },
          "& .ag-header-cell-resize::after": {
            backgroundColor: `${colors.primary[100]} !important`,
            width: "1.5px !important",
            height: "60% !important",
            top: "20% !important",
            opacity: "0.15 !important",
            transition: "opacity 0.15s ease-in-out, background-color 0.15s ease-in-out, width 0.15s ease-in-out",
          },
          "& .ag-header-cell:hover .ag-header-cell-resize::after": {
            opacity: "0.7 !important",
            backgroundColor: `${colors.primary[100]} !important`,
            width: "2px !important",
          },
          "& .ag-header-cell-resize:hover::after": {
            opacity: "1 !important",
            backgroundColor: `${colors.greenAccent?.[500] || "#4caf50"} !important`,
            width: "3px !important",
            height: "80% !important",
            top: "10% !important",
          },
        }}
      >
        <AgGridReact
          theme="legacy"
          onGridReady={onGridReady}
          onSortChanged={onSortChanged}
          onFilterChanged={onFilterChanged}
          rowData={rows}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          rowSelection="multiple"
          suppressRowClickSelection={false}
          enableCellTextSelection={true}
          onSelectionChanged={onSelectionChanged}
          pagination={false}
          rowHeight={32}
          headerHeight={36}
          floatingFiltersHeight={36}
        />
      </Box>

      {/* Replicating MUI TablePagination exactly, styled with tokens and bound to AG Grid API */}
      <TablePagination
        component="div"
        count={props.totalRecord || 0}
        page={page}
        onPageChange={handlePageChange}
        rowsPerPage={pageSize}
        onRowsPerPageChange={handleRowsPerPageChange}
        rowsPerPageOptions={[100, 250, 500]}
        sx={{
          backgroundColor: colors.grey[600],
          color: `${colors.primary[100]} !important`,
          borderTop: `1px solid ${colors.grey[700]}`,
          borderRadius: "0 0 4px 4px",
          "& .MuiTablePagination-actions .MuiIconButton-root": {
            color: `${colors.primary[100]} !important`,
          },
          "& .MuiTablePagination-selectIcon": {
            color: `${colors.primary[100]} !important`,
          },
        }}
      />
    </div>
  );
}
